import { useRef, useState } from 'react';
import { Download, FileText, Loader2, Paperclip, ShieldAlert, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import type { AttachmentView } from '@vwt/contracts';
import { Badge, Button } from '@vwt/ui';
import { api, getCsrfToken, newIdempotencyKey, useApiMutation, useApiQuery, POLL_ACTIVE, POLL_PENDING } from '../lib/api.js';
import { toastError } from '../lib/errors.js';
import { formatNumber } from '../lib/format.js';
import { useTranslation } from '../lib/i18n.js';
import { apiUrl, fetchCredentials } from '../lib/api-base.js';
import { SectionTitle } from './common.js';

export const MAX_UPLOAD_MIB = 25;
export const MAX_FILES_PER_BATCH = 10;

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${formatNumber(bytes / 1024, { maximumFractionDigits: 1 })} KB`;
  return `${formatNumber(bytes / (1024 * 1024), { maximumFractionDigits: 1 })} MB`;
}

/** Two-step upload: authorized intent, then the raw bytes into quarantine. */
export function useUploader(parentKind: 'REQUEST' | 'WORK_ITEM', parentId: string) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState<string | null>(null);

  const upload = async (files: File[]): Promise<AttachmentView[]> => {
    if (files.length > MAX_FILES_PER_BATCH) {
      toast.error(t('attachments.tooMany', { max: MAX_FILES_PER_BATCH }));
      return [];
    }
    const done: AttachmentView[] = [];
    for (const file of files) {
      if (file.size > MAX_UPLOAD_MIB * 1024 * 1024) {
        toast.error(t('attachments.tooLarge', { name: file.name, max: MAX_UPLOAD_MIB }));
        continue;
      }
      if (file.size === 0) continue;
      setUploading(file.name);
      try {
        const intent = await api.call('createUploadIntent', {
          body: { parentKind, parentId, filename: file.name, contentType: file.type || 'application/octet-stream', byteSize: file.size },
          idempotencyKey: newIdempotencyKey(),
        });
        const csrf = getCsrfToken();
        const response = await fetch(apiUrl(intent.data.uploadUrl), {
          method: 'PUT',
          credentials: fetchCredentials,
          headers: { 'Content-Type': 'application/octet-stream', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
          body: file,
        });
        const payload = (await response.json().catch(() => null)) as { data?: AttachmentView; error?: { message: string } } | null;
        if (!response.ok || !payload?.data) throw new Error(payload?.error?.message ?? `Upload failed (${response.status})`);
        done.push(payload.data);
      } catch (error) {
        toastError(error);
      }
    }
    setUploading(null);
    if (done.length) toast.success(t('attachments.uploaded', { count: done.length }));
    return done;
  };

  return { upload, uploading };
}

export function AttachButton({ onFiles, disabled, label }: { onFiles: (files: File[]) => void; disabled?: boolean; label?: string }) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
      <Button size="sm" variant="ghost" onClick={() => input.current?.click()} disabled={disabled} aria-label={label ?? t('attachments.add')}>
        <Paperclip /> {label ?? t('attachments.add')}
      </Button>
    </>
  );
}

function ScanBadge({ state }: { state: AttachmentView['scanState'] }) {
  const { t } = useTranslation();
  if (state === 'AVAILABLE') return null;
  return state === 'PENDING' ? (
    <Badge tone="outline">
      <Loader2 className="animate-spin" /> {t('attachments.scanning')}
    </Badge>
  ) : (
    <Badge tone="danger">
      <ShieldAlert /> {t('attachments.rejected')}
    </Badge>
  );
}

/** Files on a record: thumbnails for safe images, download-only for everything else. */
export function AttachmentsPanel({ parentKind, parentId, canUpload = true }: { parentKind: 'REQUEST' | 'WORK_ITEM'; parentId: string; canUpload?: boolean }) {
  const { t } = useTranslation();
  const [dragging, setDragging] = useState(false);
  const list = useApiQuery(
    'listAttachments',
    { query: { parentKind, parentId } },
    { refetchInterval: (q) => ((q.state.data?.data ?? []).some((a) => a.scanState === 'PENDING') ? POLL_PENDING : POLL_ACTIVE) },
  );
  const remove = useApiMutation('archiveAttachment', { invalidate: ['listAttachments', 'listComments'] });
  const { upload, uploading } = useUploader(parentKind, parentId);
  const files = list.data?.data ?? [];
  const refresh = () => void list.refetch();

  return (
    <section
      onDragOver={(e) => {
        if (!canUpload) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (!canUpload) return;
        e.preventDefault();
        setDragging(false);
        void upload(Array.from(e.dataTransfer.files)).then(refresh);
      }}
      className={dragging ? 'rounded-[var(--radius)] outline-2 outline-dashed outline-accent' : undefined}
    >
      <SectionTitle actions={canUpload ? <AttachButton onFiles={(f) => void upload(f).then(refresh)} disabled={!!uploading} /> : null}>
        {t('attachments.title')} {files.length ? `· ${files.length}` : ''}
      </SectionTitle>
      {uploading ? (
        <p className="mb-2 flex items-center gap-2 text-xs text-fg-muted">
          <Loader2 className="size-3.5 animate-spin" /> {t('attachments.uploading', { name: uploading })}
        </p>
      ) : null}
      {files.length === 0 ? (
        <p className="text-sm text-fg-subtle">{t('attachments.empty')}</p>
      ) : (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="attachment-list">
          {files.map((file) => (
            <li key={file.id} className="flex min-w-0 items-center gap-2.5 rounded-[var(--radius)] border border-border bg-surface p-2">
              {file.inlinePreview && file.downloadUrl ? (
                <a href={apiUrl(file.downloadUrl)} target="_blank" rel="noreferrer" className="shrink-0">
                  <img src={apiUrl(file.downloadUrl)} alt="" className="size-10 rounded object-cover" loading="lazy" />
                </a>
              ) : (
                <div className="flex size-10 shrink-0 items-center justify-center rounded bg-muted text-fg-subtle">
                  {file.scanState === 'AVAILABLE' ? <ShieldCheck className="size-4" /> : <FileText className="size-4" />}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium" title={file.filename}>
                  {file.filename}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-fg-subtle">
                  <span>{formatBytes(file.byteSize)}</span>
                  {file.uploadedBy ? <span>· {file.uploadedBy.displayName}</span> : null}
                  <ScanBadge state={file.scanState} />
                </div>
              </div>
              {file.downloadUrl ? (
                <a href={apiUrl(`${file.downloadUrl}?download=1`)} className="rounded p-1.5 text-fg-subtle hover:bg-muted hover:text-fg" aria-label={t('attachments.download')} title={t('attachments.download')}>
                  <Download className="size-4" />
                </a>
              ) : null}
              <button
                className="rounded p-1.5 text-fg-subtle hover:bg-muted hover:text-danger"
                aria-label={t('attachments.remove')}
                title={t('attachments.remove')}
                onClick={async () => {
                  try {
                    await remove.mutateAsync({ params: { id: file.id } });
                    toast.success(t('attachments.removed'));
                  } catch (error) {
                    toastError(error);
                  }
                }}
              >
                <X className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Compact file chip used inside comments and the comment composer. */
export function AttachmentChip({ file, onRemove }: { file: { id: string; filename: string; scanState: AttachmentView['scanState']; byteSize: number }; onRemove?: () => void }) {
  const { t } = useTranslation();
  const href = file.scanState === 'AVAILABLE' ? apiUrl(`/api/v1/attachments/${file.id}/content?download=1`) : null;
  const body = (
    <>
      <Paperclip className="size-3.5 shrink-0" />
      <span className="max-w-48 truncate">{file.filename}</span>
      <span className="text-fg-subtle">{formatBytes(file.byteSize)}</span>
      {file.scanState === 'PENDING' ? <Loader2 className="size-3 animate-spin" aria-label={t('attachments.scanning')} /> : null}
      {file.scanState === 'REJECTED' ? <ShieldAlert className="size-3 text-danger" aria-label={t('attachments.rejected')} /> : null}
    </>
  );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs">
      {href ? (
        <a href={href} className="inline-flex items-center gap-1.5 hover:underline">
          {body}
        </a>
      ) : (
        body
      )}
      {onRemove ? (
        <button onClick={onRemove} aria-label={t('attachments.remove')} className="text-fg-subtle hover:text-fg">
          <X className="size-3" />
        </button>
      ) : null}
    </span>
  );
}
